"use client";

import { c as _c } from "react/compiler-runtime";
import { AlertCircle, Play, StopCircle, Trash2 } from "lucide-react";
import { useRef, useState, useEffect, useCallback, useMemo } from "react";
import { ConversationScrollButton, VirtualScrollContext } from "@/components/ai-elements/conversation";
import { Loader } from "@/components/ai-elements/loader";
import { Response } from "@/components/ai-elements/response";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useI18n } from "@llmgateway/i18n/react";
import type { GroupChatMode, GroupMessage } from "@/lib/group-chat-types";
import type { ComboboxModel } from "@/lib/types";
interface GroupChatUIProps {
  mode: GroupChatMode;
  messages: GroupMessage[];
  isStreaming: boolean;
  error: string | null;
  initialPrompt: string;
  setInitialPrompt: (prompt: string) => void;
  onStart: () => void;
  onStop: () => void;
  onClear: () => void;
  selectedModels: string[];
  availableModels: ComboboxModel[];
  canStart: boolean;
}
const SCROLL_END_THRESHOLD = 80;
const GROUP_COLUMN_MIN_WIDTH = 280;
const GROUP_COLUMN_GAP = 16;
export const GroupChatUI = t0 => {
  const $ = _c(125);
  const {
    mode,
    messages,
    isStreaming,
    error,
    initialPrompt,
    setInitialPrompt,
    onStart,
    onStop,
    onClear,
    selectedModels,
    availableModels,
    canStart
  } = t0;
  const {
    t
  } = useI18n();
  const scrollRef = useRef(null);
  const [isAtEnd, setIsAtEnd] = useState(true);
  const wasAtEndRef = useRef(true);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const el = scrollRef.current;
      const nextIsAtEnd = el ? el.scrollHeight - el.scrollTop - el.clientHeight < SCROLL_END_THRESHOLD : true;
      wasAtEndRef.current = nextIsAtEnd;
      setIsAtEnd(prev => prev === nextIsAtEnd ? prev : nextIsAtEnd);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const handleScroll = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const el_0 = scrollRef.current;
      if (!el_0) {
        return;
      }
      el_0.scrollTo({
        top: el_0.scrollHeight,
        behavior: "smooth"
      });
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const scrollToEnd = t2;
  let t3;
  if ($[2] !== messages.length) {
    t3 = () => {
      if (!messages.length || !wasAtEndRef.current) {
        return;
      }
      requestAnimationFrame(() => {
        const el_1 = scrollRef.current;
        if (!el_1) {
          return;
        }
        el_1.scrollTop = el_1.scrollHeight;
        handleScroll();
      });
    };
    $[2] = messages.length;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== isStreaming || $[5] !== messages) {
    t4 = [handleScroll, isStreaming, messages];
    $[4] = isStreaming;
    $[5] = messages;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  useEffect(t3, t4);
  let t5;
  if ($[7] !== availableModels || $[8] !== t) {
    t5 = modelId => {
      if (!modelId) {
        return t("groupChat.unknownModel");
      }
      const model = availableModels.find(item => item.id === modelId);
      return model?.id ?? modelId;
    };
    $[7] = availableModels;
    $[8] = t;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const getModelLabel = t5;
  let t6;
  if ($[10] !== selectedModels) {
    t6 = modelId_0 => {
      if (!modelId_0) {
        return "bg-gray-500";
      }
      const index = selectedModels.indexOf(modelId_0);
      const colors = ["bg-blue-500", "bg-green-500", "bg-purple-500", "bg-orange-500", "bg-pink-500"];
      return colors[index] || "bg-gray-500";
    };
    $[10] = selectedModels;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const getModelColor = t6;
  let t7;
  if ($[12] !== messages) {
    t7 = messages.filter(_temp);
    $[12] = messages;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const userMessages = t7;
  let t8;
  if ($[14] !== messages) {
    t8 = messages.filter(_temp2);
    $[14] = messages;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const assistantMessages = t8;
  let sequence;
  if ($[16] !== assistantMessages) {
    sequence = new Map();
    assistantMessages.forEach((message_1, index_0) => {
      sequence.set(message_1.id, index_0 + 1);
    });
    $[16] = assistantMessages;
    $[17] = sequence;
  } else {
    sequence = $[17];
  }
  const assistantSequenceById = sequence;
  let grouped;
  if ($[18] !== assistantMessages || $[19] !== selectedModels) {
    grouped = new Map();
    for (const modelId_1 of selectedModels) {
      grouped.set(modelId_1, []);
    }
    for (const message_2 of assistantMessages) {
      if (!message_2.model) {
        continue;
      }
      const bucket = grouped.get(message_2.model);
      if (bucket) {
        bucket.push(message_2);
      }
    }
    $[18] = assistantMessages;
    $[19] = selectedModels;
    $[20] = grouped;
  } else {
    grouped = $[20];
  }
  const modelMessages = grouped;
  let counts;
  if ($[21] !== modelMessages) {
    counts = new Map();
    for (const replies of Array.from(modelMessages.values())) {
      replies.forEach((message_3, index_1) => {
        counts.set(message_3.id, index_1 + 1);
      });
    }
    $[21] = modelMessages;
    $[22] = counts;
  } else {
    counts = $[22];
  }
  const modelReplyCountByMessageId = counts;
  const lastAssistantMessage = assistantMessages[assistantMessages.length - 1] ?? null;
  let t9;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = new Intl.DateTimeFormat(undefined, {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit"
    });
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const timeFormatter = t9;
  let t10;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = timestamp => timeFormatter.format(new Date(timestamp));
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  const formatMessageTime = t10;
  let t11;
  if ($[25] !== isAtEnd) {
    t11 = {
      isAtEnd,
      scrollToEnd
    };
    $[25] = isAtEnd;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  const virtualScrollContextValue = t11;
  let t12;
  bb0: {
    if (!error) {
      t12 = false;
      break bb0;
    }
    let t13;
    if ($[27] !== error) {
      t13 = error.trim();
      $[27] = error;
      $[28] = t13;
    } else {
      t13 = $[28];
    }
    const normalizedError = t13;
    let t14;
    if ($[29] !== messages || $[30] !== normalizedError) {
      let t15;
      if ($[32] !== normalizedError) {
        t15 = message_4 => message_4.role === "assistant" && message_4.content.trim() === normalizedError;
        $[32] = normalizedError;
        $[33] = t15;
      } else {
        t15 = $[33];
      }
      t14 = messages.some(t15);
      $[29] = messages;
      $[30] = normalizedError;
      $[31] = t14;
    } else {
      t14 = $[31];
    }
    t12 = !t14;
  }
  const shouldShowGlobalError = t12;
  const totalColumnWidth = selectedModels.length * GROUP_COLUMN_MIN_WIDTH;
  const totalGapWidth = Math.max(selectedModels.length - 1, 0) * GROUP_COLUMN_GAP;
  const groupGridMinWidth = totalColumnWidth + totalGapWidth;
  let t13;
  if ($[34] !== t) {
    t13 = (message_5, index_2, isStreamingMessage) => <article key={message_5.id} className="rounded-lg border bg-background px-4 py-3"><div className="mb-3 flex items-start justify-between gap-3"><span className="text-xs text-muted-foreground">{t("groupChat.modelReplyCount", {
            count: index_2 + 1
          })}</span><span className="shrink-0 text-xs text-muted-foreground">{formatMessageTime(message_5.timestamp)}</span></div><Response className="text-sm leading-6" isStreaming={isStreamingMessage}>{message_5.content}</Response>{isStreamingMessage ? <div className="mt-3"><Loader /></div> : null}</article>;
    $[34] = t;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  const renderAnswerCard = t13;
  let t14;
  if ($[36] !== t) {
    t14 = (message_6, sequence_0, replyCount, isStreamingMessage_0) => <article key={message_6.id} className="rounded-xl border bg-card px-4 py-4 shadow-sm"><div className="mb-3 flex items-start justify-between gap-3"><div className="flex flex-wrap items-center gap-2"><span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-foreground/80">{t("groupChat.sequenceOrder", {
              order: sequence_0
            })}</span><span className="text-xs text-muted-foreground">{t("groupChat.modelReplyCount", {
              count: replyCount
            })}</span></div><span className="shrink-0 text-xs text-muted-foreground">{formatMessageTime(message_6.timestamp)}</span></div><Response className="text-sm leading-6" isStreaming={isStreamingMessage_0}>{message_6.content}</Response>{isStreamingMessage_0 ? <div className="mt-3"><Loader /></div> : null}</article>;
    $[36] = t;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  const renderDebateCard = t14;
  let t15;
  if ($[38] !== getModelColor || $[39] !== getModelLabel || $[40] !== groupGridMinWidth || $[41] !== isStreaming || $[42] !== lastAssistantMessage?.id || $[43] !== lastAssistantMessage?.model || $[44] !== modelMessages || $[45] !== renderAnswerCard || $[46] !== selectedModels || $[47] !== t) {
    t15 = () => <div className="overflow-x-auto pb-2"><div className="grid items-start gap-4" style={{
        gridTemplateColumns: `repeat(${selectedModels.length}, minmax(${GROUP_COLUMN_MIN_WIDTH}px, 1fr))`,
        minWidth: `${groupGridMinWidth}px`
      }}>{selectedModels.map(modelId_2 => {
          const replies_0 = modelMessages.get(modelId_2) ?? [];
          const isStreamingColumn = isStreaming && lastAssistantMessage?.model === modelId_2;
          return <section key={modelId_2} className="flex min-h-[320px] flex-col overflow-hidden rounded-xl border bg-card shadow-sm"><div className="flex items-center gap-3 border-b px-4 py-3"><div className={`h-2.5 w-2.5 rounded-full ${getModelColor(modelId_2)}`} /><h3 className="min-w-0 flex-1 truncate text-sm font-semibold">{getModelLabel(modelId_2)}</h3></div><div className="flex flex-1 flex-col gap-4 p-4">{replies_0.length === 0 && !isStreamingColumn ? <div className="rounded-lg border border-dashed bg-muted/30 px-4 py-6 text-sm text-muted-foreground">{t("groupChat.awaitingResponse")}</div> : null}{replies_0.map((message_7, index_3) => renderAnswerCard(message_7, index_3, isStreamingColumn && message_7.id === lastAssistantMessage?.id))}</div></section>;
        })}</div></div>;
    $[38] = getModelColor;
    $[39] = getModelLabel;
    $[40] = groupGridMinWidth;
    $[41] = isStreaming;
    $[42] = lastAssistantMessage?.id;
    $[43] = lastAssistantMessage?.model;
    $[44] = modelMessages;
    $[45] = renderAnswerCard;
    $[46] = selectedModels;
    $[47] = t;
    $[48] = t15;
  } else {
    t15 = $[48];
  }
  lastAssistantMessage?.id;
  lastAssistantMessage?.model;
  let t16;
  if ($[49] !== t15) {
    t16 = t15();
    $[49] = t15;
    $[50] = t16;
  } else {
    t16 = $[50];
  }
  const answerBoard = t16;
  lastAssistantMessage?.id;
  const t17 = `${groupGridMinWidth}px`;
  let t18;
  if ($[51] !== t17) {
    t18 = {
      minWidth: t17
    };
    $[51] = t17;
    $[52] = t18;
  } else {
    t18 = $[52];
  }
  const t19 = `repeat(${selectedModels.length}, minmax(${GROUP_COLUMN_MIN_WIDTH}px, 1fr))`;
  let t20;
  if ($[53] !== t19) {
    t20 = {
      gridTemplateColumns: t19
    };
    $[53] = t19;
    $[54] = t20;
  } else {
    t20 = $[54];
  }
  let t21;
  if ($[55] !== getModelColor || $[56] !== getModelLabel || $[57] !== selectedModels) {
    let t22;
    if ($[59] !== getModelColor || $[60] !== getModelLabel) {
      t22 = modelId_3 => <section key={modelId_3} className="rounded-xl border bg-card px-4 py-3 shadow-sm"><div className="flex items-center gap-3"><div className={`h-2.5 w-2.5 rounded-full ${getModelColor(modelId_3)}`} /><h3 className="min-w-0 truncate text-sm font-semibold">{getModelLabel(modelId_3)}</h3></div></section>;
      $[59] = getModelColor;
      $[60] = getModelLabel;
      $[61] = t22;
    } else {
      t22 = $[61];
    }
    t21 = selectedModels.map(t22);
    $[55] = getModelColor;
    $[56] = getModelLabel;
    $[57] = selectedModels;
    $[58] = t21;
  } else {
    t21 = $[58];
  }
  let t22;
  if ($[62] !== t20 || $[63] !== t21) {
    t22 = <div className="grid items-start gap-4" style={t20}>{t21}</div>;
    $[62] = t20;
    $[63] = t21;
    $[64] = t22;
  } else {
    t22 = $[64];
  }
  let t23;
  if ($[65] !== assistantMessages || $[66] !== assistantSequenceById || $[67] !== isStreaming || $[68] !== lastAssistantMessage?.id || $[69] !== modelReplyCountByMessageId || $[70] !== renderDebateCard || $[71] !== selectedModels) {
    let t24;
    if ($[73] !== assistantSequenceById || $[74] !== isStreaming || $[75] !== lastAssistantMessage?.id || $[76] !== modelReplyCountByMessageId || $[77] !== renderDebateCard || $[78] !== selectedModels) {
      t24 = (message_8, index_4) => {
        const sequence_1 = assistantSequenceById.get(message_8.id) ?? index_4 + 1;
        return <div key={message_8.id} className="grid items-start gap-4" style={{
          gridTemplateColumns: `repeat(${selectedModels.length}, minmax(${GROUP_COLUMN_MIN_WIDTH}px, 1fr))`
        }}>{selectedModels.map(modelId_4 => message_8.model === modelId_4 ? <div key={`${message_8.id}-${modelId_4}`}>{renderDebateCard(message_8, sequence_1, modelReplyCountByMessageId.get(message_8.id) ?? 1, isStreaming && message_8.id === lastAssistantMessage?.id)}</div> : <div key={`${message_8.id}-${modelId_4}`} aria-hidden="true" className="min-h-6" />)}</div>;
      };
      $[73] = assistantSequenceById;
      $[74] = isStreaming;
      $[75] = lastAssistantMessage?.id;
      $[76] = modelReplyCountByMessageId;
      $[77] = renderDebateCard;
      $[78] = selectedModels;
      $[79] = t24;
    } else {
      t24 = $[79];
    }
    t23 = assistantMessages.map(t24);
    $[65] = assistantMessages;
    $[66] = assistantSequenceById;
    $[67] = isStreaming;
    $[68] = lastAssistantMessage?.id;
    $[69] = modelReplyCountByMessageId;
    $[70] = renderDebateCard;
    $[71] = selectedModels;
    $[72] = t23;
  } else {
    t23 = $[72];
  }
  let t24;
  if ($[80] !== t23) {
    t24 = <div className="space-y-6">{t23}</div>;
    $[80] = t23;
    $[81] = t24;
  } else {
    t24 = $[81];
  }
  let t25;
  if ($[82] !== t18 || $[83] !== t22 || $[84] !== t24) {
    t25 = <div className="overflow-x-auto pb-2"><div className="space-y-4" style={t18}>{t22}{t24}</div></div>;
    $[82] = t18;
    $[83] = t22;
    $[84] = t24;
    $[85] = t25;
  } else {
    t25 = $[85];
  }
  const debateBoard = t25;
  const t26 = messages.length === 0 ? "mx-auto flex min-h-full max-w-2xl items-center justify-center px-4" : "px-4 py-4";
  let t27;
  if ($[86] !== answerBoard || $[87] !== canStart || $[88] !== debateBoard || $[89] !== initialPrompt || $[90] !== isStreaming || $[91] !== messages.length || $[92] !== mode || $[93] !== onStart || $[94] !== selectedModels.length || $[95] !== setInitialPrompt || $[96] !== t || $[97] !== userMessages) {
    t27 = messages.length === 0 ? <div className="py-10 w-full"><div className="mb-6 text-center"><h2 className="text-3xl font-semibold tracking-tight mb-2">{mode === "answer" ? t("groupChat.answerTitle") : t("groupChat.debateTitle")}</h2><p className="text-muted-foreground">{mode === "answer" ? t("groupChat.answerDescription") : t("groupChat.debateDescription")}</p></div><div className="space-y-4"><div><label className="text-sm font-medium mb-2 block">{t("groupChat.initialPrompt")}</label><Textarea value={initialPrompt} onChange={e => setInitialPrompt(e.target.value)} placeholder={t("groupChat.topicPlaceholder")} className="min-h-[120px]" disabled={isStreaming} /></div><div className="flex gap-2"><Button onClick={onStart} disabled={!canStart || !initialPrompt.trim()} className="flex-1"><Play className="size-4 mr-2" />{mode === "answer" ? t("groupChat.startAnswering") : t("groupChat.startDebating")}</Button></div>{!canStart && selectedModels.length < 2 && <Alert><AlertCircle className="h-4 w-4" /><AlertDescription>{t("groupChat.selectAtLeastTwoModels")}</AlertDescription></Alert>}</div><div className="mt-8 p-4 rounded-lg border bg-muted/50"><h3 className="font-medium mb-2">{t("groupChat.howItWorks")}</h3><ul className="text-sm text-muted-foreground space-y-1"><li>• {t("groupChat.instructionsAddModels")}</li><li>• {t("groupChat.instructionsEnterPrompt")}</li><li>•{" "}{mode === "answer" ? t("groupChat.instructionsAnswerInParallel") : t("groupChat.instructionsTakeTurns")}</li><li>•{" "}{mode === "answer" ? t("groupChat.instructionsCompareSideBySide") : t("groupChat.instructionsBuildOnResponses")}</li><li>• {t("groupChat.instructionsStopAnytime")}</li></ul></div></div> : <div className="mx-auto flex w-full max-w-[1800px] flex-col gap-4 pb-6">{userMessages.map((message_9, index_5) => <section key={message_9.id} className="rounded-xl border bg-card px-4 py-4 shadow-sm"><div className="mb-3 flex items-center justify-between gap-3"><span className="text-xs font-medium uppercase tracking-[0.08em] text-muted-foreground">{index_5 === 0 ? t("groupChat.initialPrompt") : t("groupChat.continueConversation")}</span><span className="text-xs text-muted-foreground">{formatMessageTime(message_9.timestamp)}</span></div><p className="whitespace-pre-wrap text-sm leading-6">{message_9.content}</p></section>)}{mode === "answer" ? answerBoard : debateBoard}</div>;
    $[86] = answerBoard;
    $[87] = canStart;
    $[88] = debateBoard;
    $[89] = initialPrompt;
    $[90] = isStreaming;
    $[91] = messages.length;
    $[92] = mode;
    $[93] = onStart;
    $[94] = selectedModels.length;
    $[95] = setInitialPrompt;
    $[96] = t;
    $[97] = userMessages;
    $[98] = t27;
  } else {
    t27 = $[98];
  }
  let t28;
  if ($[99] !== t26 || $[100] !== t27) {
    t28 = <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto" onScroll={handleScroll} role="log"><div className={t26}>{t27}</div></div>;
    $[99] = t26;
    $[100] = t27;
    $[101] = t28;
  } else {
    t28 = $[101];
  }
  let t29;
  if ($[102] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <ConversationScrollButton />;
    $[102] = t29;
  } else {
    t29 = $[102];
  }
  let t30;
  if ($[103] !== error || $[104] !== shouldShowGlobalError) {
    t30 = shouldShowGlobalError && error && <Alert variant="destructive" className="mb-4"><AlertCircle className="h-4 w-4" /><AlertDescription>{error}</AlertDescription></Alert>;
    $[103] = error;
    $[104] = shouldShowGlobalError;
    $[105] = t30;
  } else {
    t30 = $[105];
  }
  let t31;
  if ($[106] !== canStart || $[107] !== initialPrompt || $[108] !== isStreaming || $[109] !== messages.length || $[110] !== mode || $[111] !== onClear || $[112] !== onStart || $[113] !== onStop || $[114] !== t) {
    t31 = messages.length > 0 && <div className="flex gap-2">{isStreaming ? <Button onClick={onStop} variant="destructive" className="flex-1"><StopCircle className="size-4 mr-2" />{t("groupChat.stopConversation")}</Button> : <>{mode === "debate" ? <Button onClick={onStart} disabled={!canStart} className="flex-1"><Play className="size-4 mr-2" />{t("groupChat.continueConversation")}</Button> : <Button onClick={onStart} disabled={!initialPrompt.trim()} className="flex-1"><Play className="size-4 mr-2" />{t("groupChat.rerunAnswers")}</Button>}<Button onClick={onClear} variant="outline"><Trash2 className="size-4 mr-2" />{t("groupChat.clearConversation")}</Button></>}</div>;
    $[106] = canStart;
    $[107] = initialPrompt;
    $[108] = isStreaming;
    $[109] = messages.length;
    $[110] = mode;
    $[111] = onClear;
    $[112] = onStart;
    $[113] = onStop;
    $[114] = t;
    $[115] = t31;
  } else {
    t31 = $[115];
  }
  let t32;
  if ($[116] !== t30 || $[117] !== t31) {
    t32 = <div className="shrink-0 px-4 pb-[max(env(safe-area-inset-bottom),1rem)] pt-2 bg-background border-t">{t30}{t31}</div>;
    $[116] = t30;
    $[117] = t31;
    $[118] = t32;
  } else {
    t32 = $[118];
  }
  let t33;
  if ($[119] !== t28 || $[120] !== t32) {
    t33 = <div className="relative flex h-full min-h-0 flex-col">{t28}{t29}{t32}</div>;
    $[119] = t28;
    $[120] = t32;
    $[121] = t33;
  } else {
    t33 = $[121];
  }
  let t34;
  if ($[122] !== t33 || $[123] !== virtualScrollContextValue) {
    t34 = <VirtualScrollContext value={virtualScrollContextValue}>{t33}</VirtualScrollContext>;
    $[122] = t33;
    $[123] = virtualScrollContextValue;
    $[124] = t34;
  } else {
    t34 = $[124];
  }
  return t34;
};
function _temp(message) {
  return message.role === "user";
}
function _temp2(message_0) {
  return message_0.role === "assistant";
}